import { Layers } from 'lucide-react'
import type { CourseWithoutBlocks } from '@/types/course'
import { FailureBanner } from '@/features/ai/components/FailureBanner'
import { useReserveReturning } from '@/features/ai/useReserveReturning'
import type { StartFailure } from '@/features/ai/lib/parseStartError'
import { BlockContextChip } from './BlockContextChip'
import { useSelectedBlockRef } from './useSelectedBlockRef'
import type { useEditScope } from './useEditScope'
import type { useSidebarBusyFlow } from './useSidebarBusyFlow'
import type { BlockContext, SidebarMode } from './types'
import { useMessages } from '@/i18n'
import { messages } from './sidebarMessages'

interface Props {
  mode: SidebarMode
  /** Выделенный блок берётся здесь же (`useSelectedBlockRef`): чип — его
   *  единственный потребитель. */
  course: CourseWithoutBlocks
  /** Область правки — та же, которой кормится старт (`useEditScope`). */
  editScope: Pick<ReturnType<typeof useEditScope>, 'scope' | 'count'>
  /** Отказ ТОГО режима, в котором он случился — решает родитель. */
  failure: StartFailure | null
  onRetry: (() => void) | undefined
  onShowRun: ReturnType<typeof useSidebarBusyFlow>['handleShowRun']
}

/**
 * I-3: чип висит прямо над «Отправить», поэтому он обязан называть ОБЛАСТЬ, а
 * не выделение. С областью «весь урок» он говорил «Урок 1 · Блок 3 · Инфо»,
 * хотя уходило десять блоков и списание было в десять раз больше. Счётчик — той
 * же формулировкой, что и в экране настроек («блоков: N»), чтобы одно и то же
 * число не называлось в панели двумя разными словами.
 */
const scopeContext = (
  blockRef: BlockContext | null,
  editScope: Props['editScope'],
  m: { scopeLesson: (n: number) => string; scopeSelection: (n: number) => string },
): BlockContext | null => {
  switch (editScope.scope) {
    case 'lesson':
      return {
        label: m.scopeLesson(editScope.count),
        icon: Layers,
        lessonOrder: null,
        blockOrder: null,
      }
    // п.10: та же формулировка «блоков: N», что и в экране настроек области —
    // одно число не должно называться в панели двумя разными словами.
    case 'selection':
      return {
        label: m.scopeSelection(editScope.count),
        icon: Layers,
        lessonOrder: null,
        blockOrder: null,
      }
    case 'block':
      return blockRef
    default: {
      const _unhandled: never = editScope.scope
      return _unhandled
    }
  }
}

/**
 * Чип области правки + баннер отказа над композером — вынесены из
 * `AISidebar`, чтобы держать её файл под лимитом строк eslint.
 */
export const ComposerAlerts = ({ mode, course, editScope, failure, onRetry, onShowRun }: Props) => {
  const m = useMessages(messages)
  const blockRef = useSelectedBlockRef(course)
  // Курс знает только родитель — баннеру признак приходит пропом.
  const reserveReturning = useReserveReturning(course.id)
  const context = mode === 'edit-block' ? scopeContext(blockRef, editScope, m) : null
  return (
    <>
      {context ? (
        <div className="px-3 pb-1">
          <BlockContextChip context={context} variant="chip" />
        </div>
      ) : null}
      {failure ? (
        <FailureBanner
          failure={failure}
          className="px-4 pb-1"
          // I-5/Minor 2: решает родитель — «Повторить» достижимо только когда
          // submit() реально что-то сделает.
          onRetry={onRetry}
          onShowRun={onShowRun}
          reserveReturning={reserveReturning}
        />
      ) : null}
    </>
  )
}
